import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { ampli } from '@/ampli';
import { API_HOST, API_PROTOCOL, FRESHCHAT_TOKEN, WEBSOCKETS_PROTOCOL } from '@/env';
import {
  fetchEmailSubscriptions,
  fetchInAppSubscriptions,
  fetchNotificationActions,
  fetchNotifications,
  setUnseenNotificationsCount,
} from '@/features/Notifications';
import { setIsLoggingIn, setUser } from '@/features/user-session';
import log from '@/lib/logger';
import { type FcWidget } from '@/types/client';
import axios, { AxiosError } from 'axios';
import { host, http } from 'config';
import { useAppDispatch, useAppSelector } from 'features';
import { fetchUserGroups } from 'features/Company';
import { fetchTheme, fetchTimezones } from 'features/Settings';
import moment from 'moment-timezone';
import { fetchUser, updateProfile } from 'services/accounts';
import { LanguageEnum } from 'types/swagger';

import { UserSessionContext } from './UserSessionContext';

const sessionExpiredErrors = ['token is invalid or expired', 'token is blacklisted'];

let isRefreshing = false;
let retry = true;

const UserSessionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user, isLoggingIn } = useAppSelector((state) => state.userSession);
  const [startPage, setStartPage] = useState<string | null>(null);
  const [locale, setLocale] = useState<LanguageEnum>(LanguageEnum.Es); // aquí iría el valor default detectado;
  const [timezone, setTimezone] = useState(user?.profile.timezone || moment.tz.guess());
  const [synclogWebsocket, setSynclogWebsocket] = useState<WebSocket | null>(null);
  const [notificationsWebsocket, setNotificationsWebsocket] = useState<WebSocket | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState(true);

  const websocketErrors = useRef({
    synclog: 0,
    notifications: 0,
  });

  const resetSessionData = useCallback(() => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('currentInstance');
    http.defaults.headers.common.Authorization = null;
    http.defaults.headers.common['access-token'] = null;
  }, []);

  const refreshToken = useCallback(
    async (prevError?: AxiosError) => {
      isRefreshing = true;
      try {
        const refresh = localStorage.getItem('refresh');
        const response = await axios.post<{ access: string; refresh: string }>(
          `${API_PROTOCOL}://${host}/accounts/token/refresh/`,
          {
            refresh,
          }
        );
        if (!!response.data.access && !!response.data.refresh) {
          localStorage.setItem('access', response.data.access);
          localStorage.setItem('refresh', response.data.refresh);
          http.defaults.headers.common.Authorization = `Bearer ${response.data.access}`;
          http.defaults.headers.common['access-token'] = `Bearer ${response.data.access}`;
          if (!prevError) return { ...response.data };
          const originalRequest = prevError.config;
          if (!originalRequest) return await Promise.reject(prevError);
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          originalRequest.headers['access-token'] = `Bearer ${response.data.access}`;
          isRefreshing = false;
          log.info('Refresh token succeeded.');
          return await http(originalRequest);
        }
        return await Promise.reject(prevError as AxiosError<{ detail: string }>);
      } catch (error) {
        const e = error instanceof AxiosError ? (error as AxiosError<{ detail: string }>) : null;
        if (!e?.response) return;
        log.error('Refresh token failed:', e.response.data.detail);
        if (sessionExpiredErrors.includes(e.response.data.detail.toLowerCase())) {
          dispatch(setUser(null));
          resetSessionData();
        }
        isRefreshing = false;
        return await Promise.reject(e);
      }
    },
    [resetSessionData, dispatch]
  );

  const closeSynclogWebsocket = useCallback(() => {
    setSynclogWebsocket((websocket) => {
      if (websocket) {
        log.info('Closing Synclog websocket...');
        const prevWebsocket = websocket;
        prevWebsocket.onclose = () => {
          log.info('Synclog websocket closed by client.');
        };
        prevWebsocket.close();
      }
      return null;
    });
  }, []);

  const closeNotificationsWebsocket = useCallback(() => {
    setNotificationsWebsocket((websocket) => {
      if (websocket) {
        log.info('Closing Notifications websocket...');
        const prevWebsocket = websocket;
        prevWebsocket.onclose = () => {
          log.info('Notifications websocket closed by client.');
        };
        prevWebsocket.close();
      }
      return null;
    });
  }, []);

  const openSynclogWebsocket = useCallback(() => {
    const synclogUrl = `${WEBSOCKETS_PROTOCOL}://${host}/${WEBSOCKETS_PROTOCOL}/synclog/`;
    const newSynclogWebsocket = new WebSocket(synclogUrl);
    const authMsg = {
      type: 'authorization',
      message: {
        bearer: localStorage.getItem('access'),
      },
    };
    const synclogSubsMsg = {
      type: 'subscribe',
      topic: 'synclog',
    };
    setSynclogWebsocket((websocket) => {
      if (websocket) {
        const prevWebsocket = websocket;
        prevWebsocket.onclose = () => null;
        prevWebsocket.close();
      }
      return newSynclogWebsocket;
    });
    newSynclogWebsocket.onopen = () => {
      websocketErrors.current.synclog = 0;
      newSynclogWebsocket.send(JSON.stringify(authMsg));
      newSynclogWebsocket.send(JSON.stringify(synclogSubsMsg));
    };
    newSynclogWebsocket.onerror = (event) => {
      if (websocketErrors.current.synclog >= 5) {
        closeSynclogWebsocket();
        setTimeout(() => {
          openSynclogWebsocket();
          log.info(
            "Synclog websocket can't reconnect, attempting again in 15 seconds...",
            JSON.stringify(event)
          );
        }, 15000);
        return;
      }
      websocketErrors.current.synclog += 1;
      setTimeout(() => {
        void (async () => {
          if (!isRefreshing) await refreshToken();
          openSynclogWebsocket();
        })();
        log.info(
          'Synclog websocket error, attempting to reconnect. Error: ' + JSON.stringify(event)
        );
      }, 3000);
    };
    newSynclogWebsocket.onclose = () => {
      setTimeout(() => {
        openSynclogWebsocket();
        log.info('Synclog websocket closed by server, reconnecting...');
      }, 3000);
    };
  }, [closeSynclogWebsocket, refreshToken]);

  const openNotificationsWebsocket = useCallback(() => {
    const notificationsUrl = `${WEBSOCKETS_PROTOCOL}://${host}/${WEBSOCKETS_PROTOCOL}/notifications/`;
    const newNotificationsWebsocket = new WebSocket(notificationsUrl);
    const authMsg = {
      type: 'authorization',
      message: {
        bearer: localStorage.getItem('access'),
      },
    };

    const notificationsSubsMsg = {
      type: 'subscribe',
      topic: 'notifications',
    };
    setNotificationsWebsocket((websocket) => {
      if (websocket) {
        const prevWebsocket = websocket;
        prevWebsocket.onclose = () => null;
        prevWebsocket.close();
      }
      return newNotificationsWebsocket;
    });
    newNotificationsWebsocket.onopen = () => {
      websocketErrors.current.notifications = 0;
      newNotificationsWebsocket.send(JSON.stringify(authMsg));
      newNotificationsWebsocket.send(JSON.stringify(notificationsSubsMsg));
    };
    newNotificationsWebsocket.onerror = (event) => {
      if (websocketErrors.current.notifications >= 5) {
        closeNotificationsWebsocket();
        setTimeout(() => {
          openNotificationsWebsocket();
          log.info(
            "Synclog websocket can't reconnect, attempting again in 15 seconds...",
            JSON.stringify(event)
          );
        }, 15000);
        return;
      }
      websocketErrors.current.notifications += 1;
      setTimeout(() => {
        void (async () => {
          if (!isRefreshing) await refreshToken();
          openNotificationsWebsocket();
        })();
        log.info(
          'Synclog websocket error, attempting to reconnect. Error: ' + JSON.stringify(event)
        );
      }, 3000);
    };
    newNotificationsWebsocket.onclose = () => {
      setTimeout(() => {
        log.info('Notifications websocket closed by server, reconnecting...');
        openNotificationsWebsocket();
      }, 3000);
    };
  }, [closeNotificationsWebsocket, refreshToken]);

  const openWebsockets = useCallback(() => {
    log.info('Opening websockets...');
    openSynclogWebsocket();
    openNotificationsWebsocket();
  }, [openSynclogWebsocket, openNotificationsWebsocket]);

  const closeWebsockets = useCallback(() => {
    log.info('Closing websockets...');
    closeSynclogWebsocket();
    closeNotificationsWebsocket();
  }, [closeSynclogWebsocket, closeNotificationsWebsocket]);

  const errorResponseInterceptor: (error: AxiosError) => Promise<unknown> = useCallback(
    async (error: AxiosError) => {
      const originalRequest = error.config;
      const refresh = localStorage.getItem('refresh');
      if (
        error.message !== 'canceled' &&
        !isRefreshing &&
        refresh &&
        originalRequest &&
        error.response?.status === 401 &&
        !error.response.config.url?.includes('refresh') &&
        !error.response.config.url?.includes('logout')
      ) {
        log.info('Request failed due to old access token. Trying to refresh token...');

        try {
          return await refreshToken(error);
        } catch (tokenRefreshError) {
          if (isRefreshing && retry) {
            if (isRefreshing && retry) {
              log.info('Refresh token failed. Trying again...');
              retry = false;
              return await errorResponseInterceptor(error);
            }
            log.info('Refresh token failed again. Redirecting to login...');
            retry = true;
            http.defaults.headers.common.Authorization = null;
            http.defaults.headers.common['access-token'] = null;
            dispatch(setUser(null));
            closeWebsockets();
            resetSessionData();
            return await Promise.reject(tokenRefreshError as AxiosError);
          }
        } finally {
          isRefreshing = false;
        }
      }

      return await Promise.reject(error);
    },
    [dispatch, resetSessionData, refreshToken, closeWebsockets]
  );

  const setupInterceptors = useCallback(() => {
    log.info('Setting up interceptors...');
    http.interceptors.response.clear();
    http.interceptors.response.use(
      (response) => response,
      async (error) => await errorResponseInterceptor(error as AxiosError)
    );
  }, [errorResponseInterceptor]);

  useEffect(() => {
    setupInterceptors();
  }, [setupInterceptors]);

  const handleChangeStartPage = (userId: number, startpage: string | null) => {
    if (!userId || !startpage) return;
    const newProfile = { startpage };
    void updateProfile(userId, newProfile).then(() => {
      setStartPage(startpage);
    });
  };

  const callFetchUser = useCallback(
    async (access: string, refresh: string) => {
      if (access) {
        http.defaults.headers.common.Authorization = `Bearer ${access}`;
        http.defaults.headers.common['access-token'] = `Bearer ${access}`;
        localStorage.setItem('access', access);
      }
      if (refresh) {
        localStorage.setItem('refresh', refresh);
      }
      dispatch(setIsLoggingIn(true));
      try {
        const response = await fetchUser();
        ampli.identify(`${API_HOST}:${response.data.id}`, {
          'is enerlink': response.data.email.includes('@enerlink.c'),
          'is staff': response.data.is_staff,
          'app used': API_HOST,
        });

        const fcWidget = (window as typeof window & { fcWidget?: FcWidget }).fcWidget;
        if (fcWidget) {
          const initData = {
            token: FRESHCHAT_TOKEN,
            host: 'https://wchat.freshchat.com',
            open: false,
            locale: 'es',
            firstName: response.data.first_name || '',
            lastName: response.data.last_name || '',
            email: response.data.email || '',
            externalId: `${API_HOST}:${response.data.id}`,
            restoreId: localStorage.getItem('fc_restore_id') || null,
          };
          fcWidget.init(initData);
          const userProperties = {
            firstName: response.data.first_name || '',
            lastName: response.data.last_name || '',
            email: response.data.email || '',
            cf_client: API_HOST,
            cf_app_version: APP_VERSION || 'unknown',
          };
          fcWidget.user?.setProperties(userProperties);
          fcWidget.user?.create(userProperties, (res) => {
            log.info('Freshchat user create response:', res);
            if (res.status === 200) localStorage.setItem('fc_restore_id', res.restoreId);
          });
        }
        if (response.data.profile.startpage) {
          let updatedStartPage: string | null = response.data.profile.startpage;
          const splitStartPage = response.data.profile.startpage.split('/');
          if (updatedStartPage.includes(window.location.host.split('.')[0])) {
            splitStartPage.splice(1, 1);
            updatedStartPage = splitStartPage.join('/');
          }
          if (updatedStartPage.split('/')[1] !== 'app') {
            updatedStartPage = null;
          }
          if (splitStartPage[0] !== '') updatedStartPage = null;
          if (updatedStartPage !== response.data.profile.startpage)
            handleChangeStartPage(response.data.id, updatedStartPage);
          setStartPage(updatedStartPage);
        } else {
          setStartPage(null);
        }
        if (response.data.id) {
          dispatch(setUser(response.data));
        }
        dispatch(setUnseenNotificationsCount(response.data.profile.unseen_notifications_count));
        dispatch(setIsLoggingIn(false));
        setLocale(response.data.profile.language || LanguageEnum.Es);
        setTimezone(response.data.profile.timezone || moment.tz.guess());
      } catch (error: unknown) {
        if (
          error instanceof AxiosError &&
          error.response?.config?.url ===
            `${error.response?.config.baseURL}/accounts/token/refresh/`
        ) {
          resetSessionData();
          dispatch(setUser(null));
        }
        dispatch(setIsLoggingIn(false));
      }
    },
    [dispatch, resetSessionData]
  );

  const fetchInitialData = useCallback(async () => {
    await dispatch(fetchNotificationActions());
    await dispatch(fetchInAppSubscriptions());
    await dispatch(fetchEmailSubscriptions());
    await dispatch(fetchTimezones());
    await dispatch(fetchNotifications({ page: 1, pageSize: 10, initial: true }));
    // TODO: review global/module app data fetch based on route
    if (user?.is_staff) await dispatch(fetchUserGroups());
  }, [dispatch, user?.is_staff]);

  useEffect(() => {
    void dispatch(fetchTheme());
  }, [dispatch]);

  useEffect(() => {
    if (user) return;
    const prevToken = localStorage.getItem('access');
    const prevRefresh = localStorage.getItem('refresh');

    if (prevToken && prevRefresh) {
      void callFetchUser(prevToken, prevRefresh);
      ampli.loggedIn({ 'login method': 'active session' });
    } else {
      dispatch(setIsLoggingIn(false));
      localStorage.removeItem('access');
      localStorage.removeItem('refresh');
      localStorage.removeItem('currentInstance');

      const fcWidget = (window as typeof window & { fcWidget?: FcWidget }).fcWidget;
      if (fcWidget) {
        const initData = {
          token: FRESHCHAT_TOKEN,
          host: 'https://wchat.freshchat.com',
          open: false,
          restoreId: localStorage.getItem('fc_restore_id') || null,
        };
        fcWidget.init(initData);
      }
    }
  }, [dispatch, user, callFetchUser]);

  useEffect(() => {
    if (!user)
      return () => {
        closeWebsockets();
      };
    void fetchInitialData();
    openWebsockets();
  }, [user, closeWebsockets, openWebsockets, fetchInitialData, isLoggingIn]);

  useEffect(() => {
    moment.tz.setDefault(timezone);
  }, [timezone]);

  const contextValue = useMemo(
    () => ({
      timezone,
      setTimezone,
      locale,
      user,
      synclogWebsocket,
      notificationsWebsocket,
      startPage,
      isLoggingIn,
      callFetchUser,
      setLocale,
      setStartPage,
      shouldRedirect,
      setShouldRedirect,
    }),
    [
      timezone,
      locale,
      user,
      synclogWebsocket,
      notificationsWebsocket,
      startPage,
      isLoggingIn,
      callFetchUser,
      shouldRedirect,
    ]
  );
  return <UserSessionContext.Provider value={contextValue}>{children}</UserSessionContext.Provider>;
};

export default UserSessionProvider;
